import {
  IconBriefcase,
  IconChartBar,
  IconCurrencyDollar,
  IconDatabase,
  IconFileDescription,
  IconListDetails,
  IconLogout,
  IconMoon,
  IconSun,
} from "@tabler/icons-react"
import type { AccountInfo } from "@azure/msal-browser"
import { useEffect, useState } from "react"

type ToolRole = "tools.manager" | "tools.analytics" | "tools.archive" | "tools.inventory" 

type ResourceLink = {
  title: string
  description: string
  href: string
  icon?: typeof IconBriefcase
  imageSrc?: string
  imageAlt?: string
  requiredRoles?: ToolRole[]
}

const resourceLinks: ResourceLink[] = [
  {
    title: "GoHighLevel",
    description: "Clarifying's internal GoHighLevel workspace",
    href: "https://app.clarifying.com",
    imageSrc: "/gohighlevel.png",
    imageAlt: "GoHighLevel",
  },
  {
    title: "EnrollHere",
    description: "Access the EnrollHere dialer",
    href: "https://app.enrollhere.com/login",
    imageSrc: "/enrollhere-logo.jpeg",
    imageAlt: "EnrollHere",
  },
  {
    title: "Insurance Toolkits",
    description: "A Tool for providing Final Expense quotes",
    href: "https://app.insurancetoolkits.com/",
    imageSrc: "/insurance-toolkits.jpeg",
    imageAlt: "Insurance Toolkits",
  },
  {
    title: "Sunfire",
    description: "A Tool for providing Medicare quotes",
    href: "https://www.sunfirematrix.com/app/agent/yourmedicare",
    imageSrc: "/sunfire_logo.png",
    imageAlt: "Sunfire",
  },
  {
    title: "Life Sales Hub",
    description: "Carrier Links, Training Materials, and more",
    href: "https://enhancehealthoffice.sharepoint.com/sites/FinalExpenseTeamToolsandResources",
    icon: IconFileDescription,
  },
  {
    title: "Analytics",
    description: "Analyze sales trends and identify top performers",
    href: "https://clarifying.tools/sales-data",
    icon: IconChartBar,
    requiredRoles: ["tools.analytics"],
  },
  {
    title: "Inventory",
    description: "Manage and track inventory for Clarifying IT hardware",
    href: "https://clarifying.tools/inventory",
    icon: IconListDetails,
    requiredRoles: ["tools.inventory", "tools.manager"],
  },
  {
    title: "Telegento",
    description: "Empowering agents through AI",
    href: "https://app.telegento.com/telegento",
    imageSrc: "/clarifying-c-mark.png",
    imageAlt: "Clarifying",
  },
  {
    title: "Outlook",
    description: "Access email, calendar, and contacts",
    href: "https://outlook.office.com",
    imageSrc: "/outlook.webp",
    imageAlt: "Outlook",
  },
  {
    title: "Teams",
    description: "Chat, meet, and collaborate",
    href: "https://teams.microsoft.com",
    imageSrc: "/teams.webp",
    imageAlt: "Teams",
  },
    {
    title: "OneDrive",
    description: "Access and share your files",
    href: "https://clarifying-my.sharepoint.com/my",
    imageSrc: "/onedrive.webp",
    imageAlt: "OneDrive",
  },
  {
    title: "The Summit",
    description: "Internal corporate documentation",
    href: "https://enhancehealthoffice.sharepoint.com/sites/EnhanceHome/The%20Clarifying%20Playbook%20Vault/Forms/AllItems.aspx",
    imageSrc: "/microsoft-sharepoint.webp",
    imageAlt: "Microsoft SharePoint",
    requiredRoles: ["tools.manager"],
  },
  {
    title: "Paychex",
    description: "Access to time card, pay stubs, and more",
    href: "https://login.flex.paychex.com/",
    imageSrc: "/paychex_logo_icon_170867.webp",
    imageAlt: "Paycor",
  },
  {
    title: "Brex",
    description: "Expense Management",
    href: "https://accounts.brex.com/login",
    imageSrc: "/brexhq-logo.jpeg",
    imageAlt: "Brex",
    requiredRoles: ["tools.manager"],
  },
  {
    title: "Monday",
    description: "Project Management",
    href: "https://bizcarebenefits.monday.com/auth/login_monday/email_password",
    imageSrc: "/monday-logo.svg",
    imageAlt: "Monday",
    requiredRoles: ["tools.manager"],
  },
  {
    title: "Archive",
    description: "Retrieve archived data from Salesforce records",
    href: "https://clarifying.tools/archive",
    icon: IconDatabase,
    requiredRoles: ["tools.archive"],
  },
  {
    title: "Purchase Request",
    description: "Submit purchase requests",
    href: "https://forms.cloud.microsoft/pages/responsepage.aspx?id=k_b4MGJzR0ywS7DfurSHfTW1gpqQd_BIq00Rv-eiXABUN0I0TDU1QTZaUkZZRjM1R1lCSE1WTThDTy4u&route=shorturl",
    icon: IconCurrencyDollar,
    requiredRoles: ["tools.manager"],
  },
]

const resourceSections: Array<{ title: string; links: ResourceLink[] }> = [
  {
    title: "Sales Team",
    links: resourceLinks.slice(0, 8),
  },
  {
    title: "Employee Resources",
    links: resourceLinks.slice(8),
  },
]

function getAccountRoles(account?: AccountInfo): Set<string> | null {
  if (!account) {
    return null
  }

  const roles = account.idTokenClaims?.roles

  if (Array.isArray(roles)) {
    return new Set(roles.filter((role): role is string => typeof role === "string"))
  }

  if (typeof roles === "string") {
    return new Set([roles])
  }

  return new Set()
}

function canAccessResource(resource: ResourceLink, userRoles: Set<string> | null) {
  return (
    !userRoles ||
    !resource.requiredRoles ||
    resource.requiredRoles.some((requiredRole) => userRoles.has(requiredRole))
  )
}

const handleLeftClick = (href: string) => {
  window.open(href, "_blank", "noopener,noreferrer")
}

type AppProps = {
  account?: AccountInfo
  onSignOut?: () => void
}

type AppChromeProps = {
  isResourceDark: boolean
  onToggleTheme: () => void
  showSignOut?: boolean
  onSignOut?: () => void
}

function getBrowserPrefersDark() {
  return window.matchMedia("(prefers-color-scheme: dark)").matches
}

function AppChrome({ isResourceDark, onToggleTheme, showSignOut = true, onSignOut }: AppChromeProps) {
  return (
    <nav className="topbar" aria-label="Company">
      <a className="brand" href="/">
        <img
          className="brand-logo brand-logo-desktop"
          src="/clarifying-horizontal-lockup.png"
          alt="Clarifying"
        />
        <img
          className="brand-logo brand-logo-mobile"
          src="/clarifying-c-mark.png"
          alt=""
          aria-hidden="true"
        />
      </a>
      <div className="topbar-actions">
        <button
          className="theme-toggle-button"
          type="button"
          aria-label={`Use ${isResourceDark ? "light" : "dark"} mode for resources`}
          title={`Use ${isResourceDark ? "light" : "dark"} mode for resources`}
          aria-pressed={isResourceDark}
          onClick={onToggleTheme}
        >
          {isResourceDark ? (
            <IconSun size={18} stroke={1.9} aria-hidden="true" />
          ) : (
            <IconMoon size={18} stroke={1.9} aria-hidden="true" />
          )}
        </button>
        {showSignOut ? (
          <button
            className="sign-out-button"
            type="button"
            onClick={onSignOut}
            aria-label="Sign out"
            title="Sign out"
          >
            <IconLogout size={18} stroke={1.9} aria-hidden="true" />
          </button>
        ) : null}
      </div>
    </nav>
  )
}

export function App({ account, onSignOut }: AppProps) {
  const [resourceTheme, setResourceTheme] = useState<"light" | "dark">(() =>
    getBrowserPrefersDark() ? "dark" : "light",
  )
  const [usesBrowserTheme, setUsesBrowserTheme] = useState(true)
  const userRoles = getAccountRoles(account)
  const visibleResourceSections = resourceSections
    .map((section) => ({
      ...section,
      links: section.links.filter((resource) => canAccessResource(resource, userRoles)),
    }))
    .filter((section) => section.links.length > 0)
  const isResourceDark = resourceTheme === "dark"

  useEffect(() => {
    if (!usesBrowserTheme) {
      return
    }

    const colorSchemeQuery = window.matchMedia("(prefers-color-scheme: dark)")
    const updateResourceTheme = (event: MediaQueryListEvent) => {
      setResourceTheme(event.matches ? "dark" : "light")
    }

    setResourceTheme(colorSchemeQuery.matches ? "dark" : "light")
    colorSchemeQuery.addEventListener("change", updateResourceTheme)

    return () => {
      colorSchemeQuery.removeEventListener("change", updateResourceTheme)
    }
  }, [usesBrowserTheme])

  return (
    <main className="flex min-h-screen flex-col bg-[#f4f8ff] text-[#071b4d]">
      <section className="hero-shell">
        <AppChrome
          isResourceDark={isResourceDark}
          onSignOut={onSignOut}
          showSignOut={Boolean(onSignOut)}
          onToggleTheme={() => {
            setUsesBrowserTheme(false)
            setResourceTheme(isResourceDark ? "light" : "dark")
          }}
        />
      </section>

      <section className="resource-section" data-theme={resourceTheme}>
        <div className="resource-columns">
          {visibleResourceSections.map((section) => (
            <section className="resource-group" key={section.title}>
              <h2 className="resource-heading">{section.title}</h2>
              <div className="resource-column-grid">
                {section.links.map((resource) => {
                  const Icon = resource.icon

                  return (
                    <button className="resource-card" onClick={() => handleLeftClick(resource.href)} key={resource.title}>
                      <span className="resource-icon" aria-hidden="true">
                        {resource.imageSrc ? (
                          <img
                            className="resource-image"
                            src={resource.imageSrc}
                            alt={resource.imageAlt ?? ""}
                          />
                        ) : Icon ? (
                          <Icon size={24} stroke={1.8} />
                        ) : null}
                      </span>
                      <span className="resource-text">
                        <span className="resource-title">{resource.title}</span>
                      </span>
                    </button>
                  )
                })}
              </div>
            </section>
          ))}
        </div>
      </section>
    </main>
  )
}

export function LoginPage({ onSignIn }: { onSignIn: () => void }) {
  return (
    <main className="login-page">
      <section className="login-panel" aria-label="Sign in">
        <div className="login-card">
          <img
            className="login-logo"
            src="/clarifying-horizontal-lockup.png"
            alt="Clarifying"
          />
          <button
            className="login-button"
            type="button"
            onClick={onSignIn}
          >
            Log in to continue
          </button>
        </div>
      </section>
    </main>
  )
}
